/**
 * One copper / silkscreen layer of a PCB. Renders every track on the
 * layer and surfaces a hover-net highlight cascade — when the user
 * hovers any track, every other track on the same net lights up across
 * every layer, so multi-layer nets (split via vias) reveal themselves.
 *
 * The cascade is implemented at the canvas level: this primitive only
 * reports `(net_id | null)` upward via `onTrackHover`, and the canvas
 * threads the resulting `hoveredNetId` back down as a prop so every
 * `PCBLayer` lights up the matching tracks at the same time.
 *
 * **Rendering is bucketed by (net_id, width).** SVG strokes a single
 * width per `<path>`, so we group every track on the same net+width
 * into one path. For a typical board this collapses thousands of
 * `<path>` nodes to hundreds — hover toggles only touch the inline
 * style of a handful of buckets instead of every track, which is the
 * difference between janky 12fps pan and 60fps on a 10k-track board.
 *
 * Two passes:
 *
 *   * Glow pass — broader stroke at low opacity, only painted for the
 *     hovered net's bucket(s). Skipped when no net is hovered.
 *
 *   * Track pass — every bucket at its real width, layer colour.
 *
 * Track widths are in mm (matches IR). Stroke linecap is "round" so
 * polylines don't develop sharp elbows where segments meet.
 */
import type { PCBTrack } from "@models/circuit-ir";
import { useTheme } from "@mui/material/styles";
import { memo, useMemo } from "react";

import type { PcbLayerStyle } from "./pcbStyle";

export interface PCBLayerProps {
  layerName: string;
  tracks: PCBTrack[];
  style: PcbLayerStyle;
  visible: boolean;
  /** Net currently hovered anywhere on the board. Tracks matching this
   *  id render with a glow underlay. */
  hoveredNetId: string | null;
  onTrackHover: (netId: string | null) => void;
  /** Clearance distance in mm — when set, hovering a track paints a
   *  translucent keep-out ring around every track on that net at
   *  (track.width + 2 × clearance) wide. Renders the design-rule
   *  envelope so the user can spot violations without running DRC.
   *  Falls through to a plain glow when clearance is missing
   *  (`design_rules` not populated for this overlay). */
  clearanceMm?: number | null;
  /** Per-net stroke override — when provided, a track bucket whose
   *  `net_id` appears in this map paints with the supplied colour
   *  instead of the layer's default stroke. Drives the "colour by
   *  netclass" mode without forcing PCBLayer to know about classes
   *  itself — the canvas resolves net → class → colour once and
   *  passes the flat map down. Unmapped nets fall through to the
   *  layer colour, so partially-classified boards still render
   *  cleanly. */
  netStrokeByNetId?: Map<string, string> | null;
  /** Companion net id — when set, the layer renders the glow +
   *  clearance halo for THIS net id in addition to `hoveredNetId`.
   *  Used by diff-pair highlighting: hovering USB_DP sets
   *  companionNetId = id(USB_DN) so the partner lights up with the
   *  same visual emphasis. Falls through to no-op when null. */
  companionNetId?: string | null;
}

interface TrackBucket {
  netId: string;
  width: number;
  /** Combined SVG path-`d` for every track in this bucket. */
  d: string;
}

function trackPath(track: PCBTrack): string {
  if (track.points.length < 2) return "";
  return track.points
    .map(([x, y], i) => (i === 0 ? `M ${x} ${y}` : `L ${x} ${y}`))
    .join(" ");
}

/** Group every track on the layer into one bucket per `(net_id, width)`
 *  pair. SVG can't render different widths inside a single `<path>`, so
 *  width becomes part of the bucket key. Most boards quantise to a
 *  handful of widths (default + diff-pair + power), so the bucket
 *  count is close to the unique-net count. */
function bucketTracks(tracks: PCBTrack[]): TrackBucket[] {
  // Use a string key (net_id::width-rounded) so float widths that
  // differ by sub-µm noise still bucket together.
  const buckets = new Map<string, { netId: string; width: number; ds: string[] }>();
  for (const t of tracks) {
    const d = trackPath(t);
    if (!d) continue;
    const widthKey = t.width.toFixed(3);
    const key = `${t.net_id}::${widthKey}`;
    let bucket = buckets.get(key);
    if (!bucket) {
      bucket = { netId: t.net_id, width: t.width, ds: [] };
      buckets.set(key, bucket);
    }
    bucket.ds.push(d);
  }
  return [...buckets.values()].map((b) => ({
    netId: b.netId,
    width: b.width,
    d: b.ds.join(" "),
  }));
}

function PCBLayerImpl({
  layerName,
  tracks,
  style,
  visible,
  hoveredNetId,
  onTrackHover,
  clearanceMm,
  netStrokeByNetId,
  companionNetId,
}: PCBLayerProps) {
  const theme = useTheme();
  // Bucket once per (tracks identity) — `tracks` is already memoised
  // upstream (PCBCanvas's `tracksByLayer`), so this only re-runs when
  // the overlay genuinely changes. Recomputing on every hover would
  // defeat the entire point of the aggregation.
  const buckets = useMemo(() => bucketTracks(tracks), [tracks]);

  return (
    <g
      data-layer={layerName}
      style={{
        opacity: visible ? 1 : 0,
        pointerEvents: visible ? "auto" : "none",
        transition: theme.motion.base,
      }}
    >
      {/* Build the "highlighted nets" set ONCE for this render so
          the three render passes (clearance / glow / track-dim) all
          read the same truth. `hoveredNetId` is the primary; the
          companion is the diff-pair partner (when the cursor is on
          either half of a diff pair). */}
      {(() => {
        const highlighted = new Set<string>();
        if (hoveredNetId) highlighted.add(hoveredNetId);
        if (companionNetId) highlighted.add(companionNetId);
        if (highlighted.size === 0) return null;
        return (
          <>
            {/* Clearance halo — when design rules carry a clearance
                value, paint a soft amber ring at (track.width + 2×
                clearance) so the user can see the design-rule
                keep-out envelope on the hovered net. Painted UNDER
                the glow so the regular hover highlight still reads
                on top. Diff-pair companion also gets the halo. */}
            {clearanceMm &&
              clearanceMm > 0 &&
              buckets
                .filter((b) => highlighted.has(b.netId))
                .map((b) => (
                  <path
                    key={`clearance-${b.netId}-${b.width}`}
                    d={b.d}
                    stroke={style.glow}
                    strokeWidth={b.width + clearanceMm * 2}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    fill="none"
                    opacity={0.18}
                    style={{
                      pointerEvents: "none",
                    }}
                  />
                ))}

            {/* Glow underlay — every highlighted bucket gets one. */}
            {buckets
              .filter((b) => highlighted.has(b.netId))
              .map((b) => (
                <path
                  key={`glow-${b.netId}-${b.width}`}
                  d={b.d}
                  stroke={style.glow}
                  strokeWidth={b.width * 4}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  fill="none"
                  style={{
                    filter: "blur(0.15mm)",
                    pointerEvents: "none",
                  }}
                />
              ))}
          </>
        );
      })()}

      {/* Track pass — one path per bucket. Hover handlers fire when
          the cursor crosses ANY stroke in the bucket (SVG path
          pointer-events apply to the painted stroke, not the bbox),
          so the net-cascade fires as soon as the user is over any
          part of the net. */}
      {buckets.map((b) => {
        // A bucket is "active" if it's the hovered net OR the
        // diff-pair companion. Both get full opacity; other nets
        // dim when any highlight is on so the active pair pops.
        const isHovered = hoveredNetId === b.netId;
        const isCompanion = companionNetId === b.netId;
        const isActive = isHovered || isCompanion;
        const anyHover = hoveredNetId !== null;
        // Netclass colour override — when the canvas is in colour-by-
        // netclass mode and this net has a known class, paint with
        // the class colour. Falls through to the layer colour for
        // unclassified / "Default" nets.
        const stroke = netStrokeByNetId?.get(b.netId) ?? style.stroke;
        return (
          <path
            key={`track-${b.netId}-${b.width}`}
            d={b.d}
            stroke={stroke}
            strokeWidth={b.width}
            strokeLinecap="round"
            strokeLinejoin="round"
            fill="none"
            data-net-id={b.netId}
            style={{
              opacity: isActive ? 1 : anyHover ? 0.55 : 0.95,
              transition: theme.motion.fast,
            }}
            onPointerEnter={(e) => {
              e.stopPropagation();
              onTrackHover(b.netId);
            }}
            onPointerLeave={(e) => {
              e.stopPropagation();
              onTrackHover(null);
            }}
          />
        );
      })}
    </g>
  );
}

export const PCBLayer = memo(PCBLayerImpl);
